import { SYMBOLS, timing, variantForLevel, } from './params';
const FEEDBACK_MS = timing.feedbackMs;
const RESPONSE_WINDOW_MS = 600;
/**
 * Builder pur : construit un Stimulus avec gestion de l'historique 1-back.
 *
 * - Si `prevSymbol` est null (1er trial) : current = pick aleatoire dans le set,
 *   previous = null, isMatch = false.
 * - Sinon : avec proba 0.5 -> match (current = prevSymbol). Sinon, pick distinct
 *   dans `SYMBOLS.slice(0, setSize)` (s'il n'y a qu'un symbole et qu'on doit
 *   non-match, on retombe sur prevSymbol -> isMatch = true).
 */
export function makeStimulus(trialIdx, soaMs, level, prevSymbol) {
    const variant = variantForLevel(level);
    const setSize = variant.setSize;
    const pool = SYMBOLS.slice(0, setSize);
    let current;
    if (prevSymbol === null) {
        current = pool[Math.floor(Math.random() * pool.length)];
    }
    else if (Math.random() < 0.5) {
        current = prevSymbol;
    }
    else {
        // Pick distinct du prev quand possible.
        const others = pool.filter((s) => s !== prevSymbol);
        current = others.length > 0
            ? others[Math.floor(Math.random() * others.length)]
            : prevSymbol;
    }
    return {
        trialIdx,
        soaMs,
        current,
        previous: prevSymbol,
        isMatch: current === prevSymbol,
        setSize,
    };
}
export function createSpeedMatchAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    let prevSymbol = null;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle(pick, stim) {
        if (!resolver)
            return;
        const rt = pick === null ? RESPONSE_WINDOW_MS : Date.now() - trialStart;
        const correct = pick === null
            ? false
            : (pick === 'match') === stim.isMatch;
        const resp = {
            pick: pick ?? 'nomatch',
            isMatch: stim.isMatch,
            correct,
            reactionTimeMs: rt,
        };
        const r = resolver;
        resolver = null;
        rejecter = null;
        r(resp);
    }
    return {
        mount() {
            // Idempotent — rendu declaratif cote React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            prevSymbol = null;
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(rawStim) {
            clearTimers();
            // Override current/previous/isMatch via l'historique interne.
            const built = makeStimulus(rawStim.trialIdx, rawStim.soaMs, state.level, prevSymbol);
            const stim = { ...rawStim, ...built };
            // Memorise pour le prochain trial AVANT la promesse (1-back chain).
            prevSymbol = stim.current;
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({ subPhase: 'fixation', stim, feedback: null });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'stimulus' });
                    trialStart = Date.now();
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_response' });
                        timers.push(setTimeout(() => {
                            settle(null, stim);
                        }, RESPONSE_WINDOW_MS));
                    }, stim.soaMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, FEEDBACK_MS));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePick(pick) {
            if (state.subPhase !== 'awaiting_response')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            clearTimers();
            settle(pick, stim);
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            // Changement de niveau = nouvelle taille de set, on reset l'historique.
            prevSymbol = null;
            update({ level: newLevel, variant: v });
        },
        resetHistory() {
            prevSymbol = null;
        },
    };
}
//# sourceMappingURL=adapter.js.map